import React from 'react';
import { Route, Routes, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Users, FileCheck, Calendar, TrendingUp, CheckCircle, XCircle,
  Trash2, Eye, UserCheck, ShoppingBag, Shield, CreditCard,
  MapPin, MessageSquare, Clock, Send, Loader2, AlertCircle, Mail, Bell, ImageIcon, X,
  Banknote, Lock, Unlock, AlertTriangle, RefreshCw, Tag, Gift, Film, Pencil, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import { ActivityForm } from './GuideDashboard';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid
} from 'recharts';
import { uploadFile } from '../lib/upload';
import PhotoCropper from '../components/PhotoCropper';
import { useConfig } from '../config';

var API_URL = process.env.REACT_APP_BACKEND_URL;

/* ========== EMAIL TOOLS ========== */
function EmailTools() {
  var [testEmail, setTestEmail] = React.useState('');
  var [sending, setSending] = React.useState(false);
  var [sendingReminders, setSendingReminders] = React.useState(false);

  function sendTestEmail() {
    if (!testEmail || testEmail.indexOf('@') < 0) { toast.error('Email invalide'); return; }
    setSending(true);
    axios.post(API_URL + '/api/email/send-test', { email: testEmail }, { withCredentials: true })
      .then(function() { toast.success('Email de test envoye !'); setSending(false); })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Erreur'); setSending(false); });
  }

  function sendReminders() {
    setSendingReminders(true);
    axios.post(API_URL + '/api/email/send-reminders', {}, { withCredentials: true })
      .then(function(res) { toast.success(res.data.reminders_sent + ' rappel(s) envoye(s) pour ' + res.data.date); setSendingReminders(false); })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Erreur'); setSendingReminders(false); });
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-8" data-testid="email-tools">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
        <Mail className="h-6 w-6 inline mr-3 text-[#F9004D]" />
        Notifications Email
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-zinc-800/50 border border-white/10 rounded-lg p-5">
          <h3 className="text-white font-bold mb-3">Email de test</h3>
          <p className="text-zinc-400 text-sm mb-4">Envoyer un email de verification</p>
          <div className="flex gap-2">
            <Input type="email" value={testEmail} onChange={function(e) { setTestEmail(e.target.value); }}
              placeholder="email@example.com" className="bg-black/20 border-white/10 text-white flex-1" data-testid="test-email-input" />
            <Button onClick={sendTestEmail} disabled={sending} className="bg-[#F9004D] hover:bg-[#D00040] text-white" data-testid="send-test-email-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <div className="bg-zinc-800/50 border border-white/10 rounded-lg p-5">
          <h3 className="text-white font-bold mb-3">Rappels (J-1)</h3>
          <p className="text-zinc-400 text-sm mb-4">Envoyer les rappels pour les experiences de demain</p>
          <Button onClick={sendReminders} disabled={sendingReminders} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black w-full" data-testid="send-reminders-btn">
            {sendingReminders ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
            Envoyer les rappels
          </Button>
        </div>
      </div>
      <div className="mt-4 p-3 bg-zinc-800/30 rounded-lg">
        <p className="text-zinc-500 text-xs">
          <Mail className="h-3 w-3 inline mr-1" />
          Emails envoyes automatiquement : confirmation de reservation, notification guide, modification, annulation.
          Les rappels (J-1) doivent etre declenches manuellement.
        </p>
      </div>
    </Card>
  );
}

function formatYen(amount) {
  return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', minimumFractionDigits: 0 }).format(amount || 0);
}

/* ========== STATS ========== */
function AdminStats() {
  var cfg = useConfig();
  var [stats, setStats] = React.useState(null);
  var [hosts, setHosts] = React.useState([]);

  React.useEffect(function() {
    axios.get(API_URL + '/api/admin/stats', { withCredentials: true })
      .then(function(res) { setStats(res.data); })
      .catch(function() { toast.error('Erreur de chargement des stats'); });
    axios.get(API_URL + '/api/admin/revenue-by-host', { withCredentials: true })
      .then(function(res) { setHosts(res.data.hosts || []); })
      .catch(function() {});
  }, []);

  if (!stats) return <div className="text-white">Chargement...</div>;

  var statCards = [
    { icon: Users, color: 'text-[#F9004D]', value: stats.total_users, label: 'Utilisateurs Totaux' },
    { icon: Users, color: 'text-[#00F0FF]', value: stats.approved_guides, label: 'Guides Approuves' },
    { icon: Calendar, color: 'text-[#D4AF37]', value: stats.total_bookings, label: 'Total Reservations' },
    { icon: TrendingUp, color: 'text-green-500', value: '\u00A5' + Math.round(stats.platform_revenue || 0).toLocaleString(), label: 'Commission (' + cfg.platform_fee_percent + '%)' }
  ];

  var cardElements = [];
  for (var i = 0; i < statCards.length; i++) {
    var s = statCards[i];
    var IconComp = s.icon;
    cardElements.push(
      <Card key={i} className="bg-zinc-900/80 p-6 rounded-xl border border-white/5" data-testid={'stat-card-' + i}>
        <div className="flex items-center justify-between mb-2">
          <IconComp className={'h-8 w-8 ' + s.color} />
          <div className="text-3xl font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.value}</div>
        </div>
        <div className="text-sm text-zinc-400">{s.label}</div>
      </Card>
    );
  }

  // Monthly revenue data
  var monthly = (stats.monthly_revenue || []).map(function(m) {
    return { month: m.month.slice(5), Revenu: m.revenue, Reservations: m.bookings };
  });

  // Bookings by status pie
  var statusData = [
    { name: 'Confirmees', value: stats.confirmed_bookings || 0, color: '#22c55e' },
    { name: 'En attente', value: stats.pending_bookings || 0, color: '#eab308' },
    { name: 'Annulees', value: stats.cancelled_bookings || 0, color: '#ef4444' }
  ].filter(function(d) { return d.value > 0; });

  // Top locations
  var locData = (stats.top_locations || []).map(function(l) {
    return { location: l.location, Reservations: l.bookings };
  });

  var hostRows = [];
  for (var h = 0; h < hosts.length; h++) {
    var host = hosts[h];
    var hcRate = host.host_cancel_rate || 0;
    var hcCount = host.host_cancelled || 0;
    var hcColor = hcRate >= 20 ? 'text-red-400' : hcRate >= 10 ? 'text-yellow-400' : 'text-zinc-300';
    var hcLastMinute = host.host_cancel_last_minute || 0;
    hostRows.push(
      <tr key={host.guide_id} className="border-b border-white/5" data-testid={'host-revenue-row-' + h}>
        <td className="py-3 px-2 text-white">
          <div className="flex items-center gap-2 flex-wrap">
            <span>{host.host_name}</span>
            {host.suspended
              ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">Suspendu</span>
              : (RELIABILITY_BADGE[host.level] && <span className={'text-[10px] font-semibold px-1.5 py-0.5 rounded-full ' + RELIABILITY_BADGE[host.level].cls}>{RELIABILITY_BADGE[host.level].label}</span>)}
          </div>
        </td>
        <td className="py-3 px-2 text-zinc-400 hidden sm:table-cell">{host.host_email}</td>
        <td className="py-3 px-2 text-right text-white font-mono">{formatYen(host.total_revenue)}</td>
        <td className="py-3 px-2 text-right text-green-400 font-mono">{formatYen(host.host_earnings)}</td>
        <td className="py-3 px-2 text-right text-[#D4AF37] font-mono">{formatYen(host.platform_fee)}</td>
        <td className="py-3 px-2 text-right text-zinc-300">{host.bookings}</td>
        <td className={'py-3 px-2 text-right font-mono ' + hcColor} title={hcLastMinute + ' annulation(s) de derniere minute (<=7j)'}>
          {hcCount > 0 ? (hcCount + ' (' + hcRate + '%)') : '—'}
          {hcLastMinute > 0 ? <span className="text-red-500"> ⚠{hcLastMinute}</span> : null}
        </td>
      </tr>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-stats">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">{cardElements}</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly revenue */}
        <Card className="bg-zinc-900/50 border-white/10 p-6" data-testid="chart-monthly-revenue">
          <h3 className="text-lg font-bold text-white mb-4">Revenu mensuel (6 mois)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#a1a1aa" fontSize={12} />
              <YAxis stroke="#a1a1aa" fontSize={12} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, color: '#fff' }}
                formatter={function(v, n) { return n === 'Revenu' ? formatYen(v) : v; }} />
              <Bar dataKey="Revenu" fill="#F9004D" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Bookings by status */}
        <Card className="bg-zinc-900/50 border-white/10 p-6" data-testid="chart-bookings-status">
          <h3 className="text-lg font-bold text-white mb-4">Reservations par statut</h3>
          {statusData.length === 0 ? (
            <p className="text-zinc-500 py-12 text-center">Aucune reservation</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {statusData.map(function(entry, idx) {
                    return <Cell key={'c' + idx} fill={entry.color} />;
                  })}
                </Pie>
                <Legend wrapperStyle={{ color: '#fff' }} />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, color: '#fff' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Top locations */}
      {locData.length > 0 && (
        <Card className="bg-zinc-900/50 border-white/10 p-6" data-testid="chart-top-locations">
          <h3 className="text-lg font-bold text-white mb-4">Destinations populaires</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={locData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis type="number" stroke="#a1a1aa" fontSize={12} allowDecimals={false} />
              <YAxis type="category" dataKey="location" stroke="#a1a1aa" fontSize={12} width={90} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, color: '#fff' }} />
              <Bar dataKey="Reservations" fill="#00F0FF" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Revenue by host */}
      <Card className="bg-zinc-900/50 border-white/10 p-6" data-testid="host-revenue-table">
        <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Revenus par guide</h2>
        {hostRows.length === 0 ? (
          <p className="text-zinc-500 py-6 text-center">Aucun revenu enregistre pour le moment</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-zinc-400 text-left">
                  <th className="py-2 px-2">Guide</th>
                  <th className="py-2 px-2 hidden sm:table-cell">Email</th>
                  <th className="py-2 px-2 text-right">CA Brut</th>
                  <th className="py-2 px-2 text-right">Gain guide ({cfg.guide_fee_percent}%)</th>
                  <th className="py-2 px-2 text-right">Commission ({cfg.platform_fee_percent}%)</th>
                  <th className="py-2 px-2 text-right">Resa</th>
                  <th className="py-2 px-2 text-right">Annul. guide</th>
                </tr>
              </thead>
              <tbody>{hostRows}</tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="bg-zinc-900/50 border-white/10 p-8">
        <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Vue d'ensemble Plateforme</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div><div className="text-zinc-400 mb-2">Candidatures en Attente</div><div className="text-3xl font-bold text-[#F9004D]">{stats.pending_guides}</div></div>
          <div><div className="text-zinc-400 mb-2">Activites en attente</div><div className="text-3xl font-bold text-yellow-500">{stats.pending_activities}</div></div>
          <div><div className="text-zinc-400 mb-2">Avis clients</div><div className="text-3xl font-bold text-[#00F0FF]">{stats.total_reviews}</div></div>
          <div><div className="text-zinc-400 mb-2">Revenu Total</div><div className="text-3xl font-bold text-white">{formatYen(stats.total_revenue)}</div></div>
        </div>
      </Card>
      <EmailTools />
    </div>
  );
}

/* ========== CANDIDATURE MESSAGING ========== */
function CandidatureThread({ guideId }) {
  var [messages, setMessages] = React.useState([]);
  var [text, setText] = React.useState('');
  var [sending, setSending] = React.useState(false);
  var [open, setOpen] = React.useState(false);

  function loadMessages() {
    axios.get(API_URL + '/api/admin/guides/' + guideId + '/messages', { withCredentials: true })
      .then(function(r) { setMessages(r.data); });
  }

  function toggleOpen() {
    if (!open) loadMessages();
    setOpen(function(v) { return !v; });
  }

  function sendMessage() {
    if (!text.trim()) return;
    setSending(true);
    axios.post(API_URL + '/api/admin/guides/' + guideId + '/messages', { content: text }, { withCredentials: true })
      .then(function() { setText(''); loadMessages(); toast.success('Message envoyé'); })
      .catch(function() { toast.error('Erreur envoi'); })
      .finally(function() { setSending(false); });
  }

  return (
    <div className="mt-4 border-t border-white/10 pt-4">
      <button onClick={toggleOpen} className="text-sm text-[#00F0FF] hover:underline flex items-center gap-1">
        <MessageSquare className="h-4 w-4" /> {open ? 'Masquer la messagerie' : 'Envoyer un message au candidat'}
        {messages.length > 0 && !open && <span className="ml-1 bg-[#F9004D] text-white text-xs px-1.5 py-0.5 rounded-full">{messages.length}</span>}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="max-h-48 overflow-y-auto space-y-2 bg-black/20 rounded-lg p-3">
            {messages.length === 0 && <p className="text-zinc-500 text-sm text-center">Aucun message</p>}
            {messages.map(function(m) {
              var isImg = m.attachment_url && /\.(jpg|jpeg|png|gif|webp)$/i.test(m.attachment_url);
              return (
                <div key={m.message_id} className={'flex ' + (m.sender === 'admin' ? 'justify-end' : 'justify-start')}>
                  <div className={'max-w-xs px-3 py-2 rounded-lg text-sm ' + (m.sender === 'admin' ? 'bg-[#F9004D] text-white' : 'bg-zinc-700 text-zinc-200')}>
                    <p className="font-semibold text-xs opacity-70 mb-1">{m.sender === 'admin' ? 'Admin' : m.sender_name}</p>
                    {m.content && <p>{m.content}</p>}
                    {m.attachment_url && (
                      isImg
                        ? <a href={m.attachment_url} target="_blank" rel="noreferrer"><img src={m.attachment_url} alt="pj" className="mt-2 max-w-full rounded-lg max-h-40 object-contain" /></a>
                        : <a href={m.attachment_url} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-1 underline text-xs opacity-90"><Eye className="h-3 w-3" /> Voir le document</a>
                    )}
                    <p className="text-xs opacity-50 mt-1">{new Date(m.created_at).toLocaleString('fr-FR')}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2">
            <textarea
              value={text}
              onChange={function(e) { setText(e.target.value); }}
              placeholder="Écrivez votre message..."
              rows={2}
              className="flex-1 bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none focus:border-[#F9004D] outline-none"
            />
            <Button onClick={sendMessage} disabled={sending || !text.trim()} className="bg-[#F9004D] hover:bg-[#D00040] text-white self-end">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== CANDIDATURE DOCUMENTS ========== */
function CandidatureDocuments({ app, onUpdated }) {
  var [uploading, setUploading] = React.useState(null);
  var [pendingPhoto, setPendingPhoto] = React.useState(null);

  function handleUpload(field, e) {
    var file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    // La photo de profil passe par le recadrage imposé, comme côté guide.
    if (field === 'profile_photo_url') { setPendingPhoto(file); return; }
    send(field, file);
  }

  function send(field, file) {
    setUploading(field);
    uploadFile(file)
      .then(function(url) {
        var body = {};
        body[field] = url;
        return axios.put(API_URL + '/api/admin/guides/' + app.guide_id + '/documents', body, { withCredentials: true });
      })
      .then(function() { toast.success('Document mis à jour'); onUpdated(); })
      .catch(function() { toast.error("Échec de l'upload"); })
      .finally(function() { setUploading(null); });
  }

  // Photo de profil : on envoie le recadrage ET l'originale, pour que le guide
  // puisse ensuite refaire son cadrage lui-même depuis son espace.
  function sendPhoto(croppedFile, crop) {
    setUploading('profile_photo_url');
    Promise.all([uploadFile(croppedFile), uploadFile(pendingPhoto)])
      .then(function(urls) {
        return axios.put(API_URL + '/api/admin/guides/' + app.guide_id + '/documents', {
          profile_photo_url: urls[0],
          profile_photo_original_url: urls[1],
          profile_photo_crop: crop
        }, { withCredentials: true });
      })
      .then(function() { toast.success('Photo mise à jour'); setPendingPhoto(null); onUpdated(); })
      .catch(function() { toast.error("Échec de l'upload"); })
      .finally(function() { setUploading(null); });
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">Documents</p>
      <div className="flex flex-wrap gap-3">
        {/* Photo profil */}
        <div className="flex items-center gap-2">
          {app.profile_photo_url
            ? <a href={app.profile_photo_url} target="_blank" rel="noreferrer" className="text-xs text-[#00F0FF] underline flex items-center gap-1"><Eye className="h-3 w-3" /> Photo profil</a>
            : <span className="text-xs text-zinc-500">Pas de photo</span>}
          <label className="cursor-pointer text-xs bg-zinc-700 hover:bg-zinc-600 text-zinc-300 px-2 py-1 rounded flex items-center gap-1">
            {uploading === 'profile_photo_url' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
            {app.profile_photo_url ? 'Remplacer' : 'Ajouter'}
            <input type="file" accept="image/*" onChange={function(e) { handleUpload('profile_photo_url', e); }} className="hidden" disabled={!!uploading} />
          </label>
        </div>
        {/* Pièce d'identité */}
        <div className="flex items-center gap-2">
          {app.id_document_url
            ? <a href={app.id_document_url} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] underline flex items-center gap-1"><Eye className="h-3 w-3" /> Pièce d'identité</a>
            : <span className="text-xs text-zinc-500">Pas de pièce d'identité</span>}
          <label className="cursor-pointer text-xs bg-zinc-700 hover:bg-zinc-600 text-zinc-300 px-2 py-1 rounded flex items-center gap-1">
            {uploading === 'id_document_url' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
            {app.id_document_url ? 'Remplacer' : 'Ajouter'}
            <input type="file" accept="image/*,application/pdf" onChange={function(e) { handleUpload('id_document_url', e); }} className="hidden" disabled={!!uploading} />
          </label>
        </div>
      </div>
      {pendingPhoto && (
        <PhotoCropper
          file={pendingPhoto}
          uploading={uploading === 'profile_photo_url'}
          onCancel={function() { setPendingPhoto(null); }}
          onCropped={sendPhoto}
        />
      )}
    </div>
  );
}

/* ========== PENDING APPLICATIONS ========== */
function PendingApplications() {
  var [applications, setApplications] = React.useState([]);
  var [fetchError, setFetchError] = React.useState(null);

  React.useEffect(function() { fetchApplications(); }, []);

  function fetchApplications() {
    setFetchError(null);
    axios.get(API_URL + '/api/admin/guides/pending', { withCredentials: true })
      .then(function(res) { setApplications(res.data); })
      .catch(function(err) {
        var msg = err.response?.status === 403 ? 'Non autorisé (403) — êtes-vous bien connecté en tant qu\'admin ?' : (err.response?.data?.detail || 'Erreur de chargement ' + (err.response?.status || ''));
        setFetchError(msg);
        toast.error(msg);
      });
  }

  function handleApprove(guideId) {
    axios.put(API_URL + '/api/admin/guides/' + guideId + '/approve', {}, { withCredentials: true })
      .then(function() { toast.success('Guide approuve'); fetchApplications(); })
      .catch(function() { toast.error('Erreur'); });
  }

  function handleReject(guideId) {
    axios.put(API_URL + '/api/admin/guides/' + guideId + '/reject', {}, { withCredentials: true })
      .then(function() { toast.success('Guide rejete'); fetchApplications(); })
      .catch(function() { toast.error('Erreur'); });
  }

  var appCards = [];
  for (var i = 0; i < applications.length; i++) {
    var app = applications[i];
    var langTags = [];
    if (app.languages_spoken) {
      for (var j = 0; j < app.languages_spoken.length; j++) {
        langTags.push(<span key={app.languages_spoken[j]} className="px-2 py-1 bg-white/5 border border-white/10 rounded text-xs text-zinc-300">{app.languages_spoken[j].toUpperCase()}</span>);
      }
    }
    appCards.push(
      <Card key={app.guide_id} className="bg-zinc-800/50 border-zinc-700 p-6">
        <div className="flex items-start justify-between gap-4">
          {/* Photo */}
          {app.profile_photo_url && (
            <img src={app.profile_photo_url} alt={app.user_name} className="w-16 h-16 rounded-full object-cover object-top flex-shrink-0" />
          )}
          <div className="flex-1 space-y-3">
            {/* Identité du candidat */}
            <div className="bg-zinc-700/40 rounded-xl p-4 border border-white/10 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-zinc-500 uppercase tracking-widest">Candidat</span>
              </div>
              {(app.user_first_name || app.user_last_name) ? (
                <div className="flex gap-4">
                  <div>
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider block">Prénom</span>
                    <span className="text-white font-semibold text-base">{app.user_first_name || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider block">Nom</span>
                    <span className="text-white font-semibold text-base">{app.user_last_name || '—'}</span>
                  </div>
                </div>
              ) : (
                <span className="text-white font-semibold text-base">{app.user_name || '—'}</span>
              )}
              <div>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider block">Email</span>
                <a href={'mailto:' + app.user_email} className="text-[#F9004D] hover:underline text-sm font-medium">{app.user_email || '—'}</a>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">{langTags}</div>
            <p className="text-zinc-300 text-sm">{app.bio?.fr || app.bio?.en}</p>
            <div className="text-sm text-zinc-400">Zones : {app.areas?.join(', ') || '—'}</div>
            <div className="text-sm text-zinc-400">Visa : {app.visa_status || '—'}</div>

            {/* Documents */}
            <CandidatureDocuments app={app} onUpdated={fetchApplications} />

            {/* Messaging */}
            <CandidatureThread guideId={app.guide_id} />
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <Button onClick={function(id) { return function() { handleApprove(id); }; }(app.guide_id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={'approve-guide-' + app.guide_id}>
              <CheckCircle className="h-4 w-4 mr-2" /> Approuver
            </Button>
            <Button onClick={function(id) { return function() { handleReject(id); }; }(app.guide_id)} variant="destructive" data-testid={'reject-guide-' + app.guide_id}>
              <XCircle className="h-4 w-4 mr-2" /> Rejeter
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-8" data-testid="pending-applications">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Candidatures en Attente</h2>
        <Button onClick={fetchApplications} variant="outline" className="border-white/20 text-zinc-300 hover:text-white text-sm">Actualiser</Button>
      </div>
      {fetchError
        ? <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 text-red-300">{fetchError}</div>
        : applications.length === 0
          ? <p className="text-zinc-400 text-center py-8">Aucune candidature en attente</p>
          : <div className="space-y-4">{appCards}</div>}
    </Card>
  );
}

/* ========== ACTIVITY VALIDATION ========== */
function ActivityValidation() {
  var [pending, setPending] = React.useState([]);
  var [withChanges, setWithChanges] = React.useState([]);
  var [loading, setLoading] = React.useState(true);

  React.useEffect(function() { fetchAll(); }, []);

  function fetchAll() {
    setLoading(true);
    Promise.all([
      axios.get(API_URL + '/api/admin/activities/pending', { withCredentials: true }),
      axios.get(API_URL + '/api/admin/activities/with-pending-changes', { withCredentials: true })
    ]).then(function(results) {
      setPending(results[0].data);
      setWithChanges(results[1].data);
      setLoading(false);
    }).catch(function() { toast.error('Erreur de chargement'); setLoading(false); });
  }

  function reviewActivity(activityId, action) {
    axios.put(API_URL + '/api/admin/activities/' + activityId + '/review', { action: action }, { withCredentials: true })
      .then(function() { toast.success(action === 'approve' ? 'Activite approuvee' : 'Activite rejetee'); fetchAll(); })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Erreur'); });
  }

  function approveChanges(activityId) {
    axios.put(API_URL + '/api/admin/activities/' + activityId + '/approve-changes', {}, { withCredentials: true })
      .then(function() { toast.success('Modifications approuvees'); fetchAll(); })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Erreur'); });
  }

  function rejectChanges(activityId) {
    axios.put(API_URL + '/api/admin/activities/' + activityId + '/reject-changes', {}, { withCredentials: true })
      .then(function() { toast.success('Modifications rejetees'); fetchAll(); })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Erreur'); });
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" /></div>;

  var pendingCards = [];
  for (var i = 0; i < pending.length; i++) {
    var act = pending[i];
    pendingCards.push(
      <Card key={act.activity_id} className="bg-zinc-800/50 border-yellow-500/20 p-6" data-testid={'pending-activity-' + act.activity_id}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-[#F9004D]/20 text-[#F9004D] rounded-full text-sm">{act.location}</span>
              <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">Nouvelle activite</span>
            </div>
            <h3 className="text-lg font-bold text-white">{act.name?.fr || act.name?.en || 'Sans nom'}</h3>
            <p className="text-zinc-400 text-sm mt-1">{act.description?.fr || act.description?.en || ''}</p>
            <div className="flex gap-4 mt-3 text-sm text-zinc-500">
              <span>Adulte: {formatYen(act.adult_rate)}</span>
              <span>Enfant: {act.children_allowed ? formatYen(act.child_rate) : 'Interdit'}</span>
              <span>Max: {act.max_participants} pers.</span>
            </div>
            <p className="text-zinc-500 text-xs mt-2">Guide: {act.guide_name || act.guide_id}</p>
          </div>
          <div className="flex flex-col gap-2 ml-4">
            <Button onClick={function(id) { return function() { reviewActivity(id, 'approve'); }; }(act.activity_id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={'approve-activity-' + act.activity_id}>
              <CheckCircle className="h-4 w-4 mr-2" /> Approuver
            </Button>
            <Button onClick={function(id) { return function() { reviewActivity(id, 'reject'); }; }(act.activity_id)} variant="destructive" data-testid={'reject-activity-' + act.activity_id}>
              <XCircle className="h-4 w-4 mr-2" /> Rejeter
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  var changesCards = [];
  for (var k = 0; k < withChanges.length; k++) {
    var chAct = withChanges[k];
    var changes = chAct.pending_changes || {};
    var changeKeys = Object.keys(changes);
    var changeItems = [];
    for (var m = 0; m < changeKeys.length; m++) {
      changeItems.push(
        <div key={changeKeys[m]} className="text-sm">
          <span className="text-zinc-500">{changeKeys[m]}:</span>
          <span className="text-[#00F0FF] ml-2">{JSON.stringify(changes[changeKeys[m]])}</span>
        </div>
      );
    }
    changesCards.push(
      <Card key={chAct.activity_id} className="bg-zinc-800/50 border-orange-500/20 p-6" data-testid={'changes-activity-' + chAct.activity_id}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-[#F9004D]/20 text-[#F9004D] rounded-full text-sm">{chAct.location}</span>
              <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs">Modifications</span>
            </div>
            <h3 className="text-lg font-bold text-white">{chAct.name?.fr || chAct.name?.en || 'Sans nom'}</h3>
            <div className="mt-3 p-3 bg-black/30 rounded-lg space-y-1">
              <p className="text-orange-400 text-xs font-bold mb-2">Changements proposes:</p>
              {changeItems}
            </div>
            <p className="text-zinc-500 text-xs mt-2">Guide: {chAct.guide_name || chAct.guide_id}</p>
          </div>
          <div className="flex flex-col gap-2 ml-4">
            <Button onClick={function(id) { return function() { approveChanges(id); }; }(chAct.activity_id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={'approve-changes-' + chAct.activity_id}>
              <CheckCircle className="h-4 w-4 mr-2" /> Approuver
            </Button>
            <Button onClick={function(id) { return function() { rejectChanges(id); }; }(chAct.activity_id)} variant="destructive" data-testid={'reject-changes-' + chAct.activity_id}>
              <XCircle className="h-4 w-4 mr-2" /> Rejeter
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  var totalPending = pending.length + withChanges.length;

  return (
    <div className="space-y-8" data-testid="activity-validation">
      <Card className="bg-zinc-900/50 border-white/10 p-8">
        <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
          Validation des Activites ({totalPending} en attente)
        </h2>

        {totalPending === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="h-16 w-16 text-green-500/30 mx-auto mb-4" />
            <p className="text-zinc-400 text-lg">Aucune activite en attente de validation</p>
          </div>
        ) : (
          <div className="space-y-6">
            {pending.length > 0 && (
              <div>
                <h3 className="text-lg text-yellow-400 font-bold mb-4">Nouvelles activites ({pending.length})</h3>
                <div className="space-y-4">{pendingCards}</div>
              </div>
            )}
            {withChanges.length > 0 && (
              <div>
                <h3 className="text-lg text-orange-400 font-bold mb-4">Modifications en attente ({withChanges.length})</h3>
                <div className="space-y-4">{changesCards}</div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ========== ADMIN MESSAGING ========== */
function AdminConversations() {
  var [conversations, setConversations] = React.useState([]);
  var [selectedConv, setSelectedConv] = React.useState(null);
  var [messages, setMessages] = React.useState([]);
  var [loading, setLoading] = React.useState(true);

  React.useEffect(function() {
    axios.get(API_URL + '/api/admin/conversations', { withCredentials: true })
      .then(function(res) { setConversations(res.data); setLoading(false); })
      .catch(function() { toast.error('Erreur de chargement'); setLoading(false); });
  }, []);

  function loadMessages(convId) {
    setSelectedConv(convId);
    axios.get(API_URL + '/api/admin/conversations/' + convId + '/messages', { withCredentials: true })
      .then(function(res) {
        var data = res.data;
        setMessages(data.messages || data);
      })
      .catch(function() { toast.error('Erreur de chargement'); });
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" /></div>;

  var convList = [];
  for (var i = 0; i < conversations.length; i++) {
    var c = conversations[i];
    convList.push(
      <div key={c.conversation_id} onClick={function(id) { return function() { loadMessages(id); }; }(c.conversation_id)}
        className={'p-4 cursor-pointer border-b border-white/5 hover:bg-zinc-800/50 ' + (selectedConv === c.conversation_id ? 'bg-zinc-800/50 border-l-2 border-l-[#F9004D]' : '')}
        data-testid={'admin-conv-' + c.conversation_id}>
        <div className="flex justify-between items-start">
          <div>
            <p className="text-white font-bold text-sm">{c.traveler_name || 'Voyageur'}</p>
            <p className="text-[#00F0FF] text-xs">{c.guide_name || 'Guide'}</p>
          </div>
          <span className="text-zinc-500 text-xs">{c.message_count || 0} msg</span>
        </div>
        <p className="text-zinc-500 text-xs mt-1">{(c.activity_name && typeof c.activity_name === 'object' ? (c.activity_name.fr || c.activity_name.en) : c.activity_name) || 'Activite'}</p>
      </div>
    );
  }

  var msgList = [];
  for (var j = 0; j < messages.length; j++) {
    var m = messages[j];
    var roleLabel = m.sender_role === 'guide' ? 'Guide' : 'Voyageur';
    var roleColor = m.sender_role === 'guide' ? 'text-[#00F0FF]' : 'text-[#F9004D]';
    msgList.push(
      <div key={m.message_id || j} className="bg-zinc-800/50 p-3 rounded-lg">
        <div className="flex justify-between items-center mb-1">
          <span className={'text-xs font-bold ' + roleColor}>{roleLabel}: {m.sender_name || ''}</span>
          <span className="text-xs text-zinc-500">{m.created_at ? new Date(m.created_at).toLocaleString('fr-FR') : ''}</span>
        </div>
        <p className="text-zinc-300 text-sm">{m.content}</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-conversations">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
        Supervision des Messages ({conversations.length} conversations)
      </h2>
      {conversations.length === 0 ? (
        <Card className="bg-zinc-900/50 border-white/10 p-8 text-center">
          <MessageSquare className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400">Aucune conversation</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ minHeight: '500px' }}>
          <Card className="bg-zinc-900/50 border-white/10 overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-white font-bold text-sm">Conversations ({conversations.length})</h3>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '450px' }}>{convList}</div>
          </Card>
          <Card className="md:col-span-2 bg-zinc-900/50 border-white/10 flex flex-col">
            {selectedConv ? (
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '500px' }}>
                <div className="flex items-center gap-2 mb-4 p-2 bg-zinc-800/30 rounded-lg">
                  <Eye className="h-4 w-4 text-zinc-500" />
                  <span className="text-zinc-500 text-xs">Mode lecture seule - Supervision admin</span>
                </div>
                {msgList.length === 0 ? <p className="text-zinc-500 text-center py-8">Aucun message</p> : msgList}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-zinc-500"><p>Selectionnez une conversation</p></div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

/* ========== CAGNOTTES (CADEAUX PLATEFORME) ========== */
function ManageWallets() {
  var [rows, setRows] = React.useState([]);
  var [fx, setFx] = React.useState(null);
  var [busy, setBusy] = React.useState(false);
  var [form, setForm] = React.useState({ user_id: '', amount_eur: '', label: '' });

  React.useEffect(function() { fetchAll(); }, []);

  function fetchAll() {
    axios.get(API_URL + '/api/admin/wallets', { withCredentials: true })
      .then(function(r) { setRows(r.data || []); })
      .catch(function() { toast.error('Erreur de chargement'); });
    axios.get(API_URL + '/api/admin/fx-rates', { withCredentials: true })
      .then(function(r) { setFx(r.data); })
      .catch(function() { setFx(null); });
  }

  function adjust() {
    var montant = parseFloat(form.amount_eur);
    if (!form.user_id.trim() || !montant) { toast.error('Identifiant et montant requis'); return; }
    setBusy(true);
    axios.post(API_URL + '/api/admin/wallets/adjust', {
      user_id: form.user_id.trim(), amount_eur: montant, label: form.label
    }, { withCredentials: true })
      .then(function(r) {
        toast.success('Nouveau solde : ' + r.data.balance_eur + ' EUR');
        setForm({ user_id: '', amount_eur: '', label: '' });
        fetchAll();
      })
      .catch(function(e) { toast.error((e.response && e.response.data && e.response.data.detail) || 'Erreur'); })
      .finally(function() { setBusy(false); });
  }

  function refreshFx() {
    setBusy(true);
    axios.post(API_URL + '/api/admin/fx-rates/refresh', {}, { withCredentials: true })
      .then(function(r) { setFx(r.data); toast.success('Taux mis a jour'); })
      .catch(function() { toast.error('Erreur'); })
      .finally(function() { setBusy(false); });
  }

  var total = rows.reduce(function(acc, r) { return acc + (r.balance_eur || 0); }, 0);
  // Un taux vieux de plus de 48 h signale que le service de change est en panne.
  var fxVieux = fx && fx.age_hours !== null && fx.age_hours > 48;

  return (
    <div className="space-y-6" data-testid="manage-wallets">
      {/* Engagement total : c'est de l'argent que nous devrons verser aux guides */}
      <Card className="bg-zinc-900/50 border-white/10 p-6">
        <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Cagnottes</h2>
        <p className="text-zinc-500 text-sm mb-4">
          Credits offerts aux voyageurs. Quand ils les depensent, nous versons la part
          du guide depuis notre solde Stripe : ce total est donc un engagement.
        </p>
        <div className="flex flex-wrap gap-6">
          <div>
            <p className="text-zinc-400 text-xs">Engagement total</p>
            <p className="text-3xl font-bold text-[#00F0FF]">{total.toFixed(2)} EUR</p>
          </div>
          <div>
            <p className="text-zinc-400 text-xs">Voyageurs concernes</p>
            <p className="text-3xl font-bold text-white">{rows.length}</p>
          </div>
        </div>
      </Card>

      {/* Taux de change : c'est lui qui convertit la cagnotte au paiement */}
      <Card className={'p-6 ' + (fxVieux ? 'bg-red-950/30 border border-red-500/40' : 'bg-zinc-900/50 border-white/10')}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-bold text-white mb-1">Taux de change</h3>
            {fx && fx.rates ? (
              <p className="text-zinc-400 text-sm">
                1 EUR = <span className="text-white font-semibold">{fx.rates.jpy}</span> JPY
                {' · '}source : {fx.source}
                {fx.rate_date ? ' · date : ' + fx.rate_date : ''}
                {fx.age_hours !== null && fx.age_hours !== undefined ? ' · il y a ' + fx.age_hours + ' h' : ''}
              </p>
            ) : <p className="text-zinc-500 text-sm">Indisponible</p>}
            {fxVieux && <p className="text-red-400 text-sm mt-1">Taux anciens : le service de change est peut-etre en panne.</p>}
          </div>
          <Button onClick={refreshFx} disabled={busy} variant="outline" className="border-white/20 text-white hover:bg-white/10">
            Rafraichir
          </Button>
        </div>
      </Card>

      {/* Ajustement manuel (geste commercial, correction) */}
      <Card className="bg-zinc-900/50 border-white/10 p-6">
        <h3 className="text-lg font-bold text-white mb-3">Crediter / debiter</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="text-zinc-400 text-xs">Identifiant du voyageur</label>
            <Input value={form.user_id} onChange={function(e) { setForm(Object.assign({}, form, { user_id: e.target.value })); }}
              placeholder="user_xxxxxxxx" className="bg-zinc-800 border-white/10 text-white" />
          </div>
          <div>
            <label className="text-zinc-400 text-xs">Montant EUR (negatif = debit)</label>
            <Input type="number" step="0.01" value={form.amount_eur}
              onChange={function(e) { setForm(Object.assign({}, form, { amount_eur: e.target.value })); }}
              className="bg-zinc-800 border-white/10 text-white" />
          </div>
          <Button onClick={adjust} disabled={busy} className="bg-[#F9004D] hover:bg-[#D00040] text-white">
            Appliquer
          </Button>
          <div className="sm:col-span-4">
            <label className="text-zinc-400 text-xs">Motif (apparait dans l&apos;historique du voyageur)</label>
            <Input value={form.label} onChange={function(e) { setForm(Object.assign({}, form, { label: e.target.value })); }}
              placeholder="Geste commercial, correction..." className="bg-zinc-800 border-white/10 text-white" />
          </div>
        </div>
      </Card>

      <Card className="bg-zinc-900/50 border-white/10 p-6">
        <h3 className="text-lg font-bold text-white mb-4">Soldes ({rows.length})</h3>
        {rows.length === 0 ? (
          <p className="text-zinc-400 text-center py-6">Aucune cagnotte pour le moment</p>
        ) : (
          <div className="space-y-2">
            {rows.map(function(r) {
              return (
                <div key={r.user_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-semibold truncate">{r.name || r.user_id}</p>
                    <p className="text-zinc-500 text-xs truncate">{r.email} · {r.movements} mouvement(s)</p>
                    <p className="text-zinc-600 text-xs font-mono">{r.user_id}</p>
                  </div>
                  <span className="text-xl font-bold text-[#00F0FF] flex-shrink-0">{(r.balance_eur || 0).toFixed(2)} EUR</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ========== MISE EN AVANT D'UN HOTE ========== */
function FeatureHostControl({ host, onUpdated }) {
  var [kind, setKind] = React.useState(host.featured_kind || 'none');
  var [rank, setRank] = React.useState(host.featured_rank || 1);
  var [until, setUntil] = React.useState(host.featured_until || '');
  var [saving, setSaving] = React.useState(false);

  function save() {
    setSaving(true);
    axios.put(API_URL + '/api/admin/hosts/' + host.guide_id + '/feature', {
      featured_kind: kind,
      featured_rank: kind === 'none' ? null : parseInt(rank, 10) || 1,
      featured_until: kind === 'none' ? null : (until || null)
    }, { withCredentials: true })
      .then(function() { toast.success('Mise en avant enregistrée'); onUpdated(); })
      .catch(function(e) { toast.error((e.response && e.response.data && e.response.data.detail) || 'Erreur'); })
      .finally(function() { setSaving(false); });
  }

  // Une mise en avant dont la date est passée n'a plus d'effet : on le dit.
  var expiree = !!until && kind !== 'none' && until < new Date().toISOString().slice(0, 10);

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="feature-control">
      <select
        value={kind}
        onChange={function(e) { setKind(e.target.value); }}
        className="bg-zinc-800 border border-white/10 rounded px-2 py-1 text-zinc-200"
      >
        <option value="none">Pas de mise en avant</option>
        <option value="star">Guide star</option>
        <option value="creator">Créateur / youtubeur</option>
      </select>
      {kind !== 'none' && (
        <>
          <label className="text-zinc-500">Rang</label>
          <select
            value={rank}
            onChange={function(e) { setRank(e.target.value); }}
            className="bg-zinc-800 border border-white/10 rounded px-2 py-1 text-zinc-200"
          >
            <option value="1">1 (tout en haut)</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>
          <label className="text-zinc-500">Jusqu'au</label>
          <input
            type="date"
            value={until}
            onChange={function(e) { setUntil(e.target.value); }}
            className="bg-zinc-800 border border-white/10 rounded px-2 py-1 text-zinc-200"
          />
          {!until && <span className="text-zinc-600">(sans limite)</span>}
          {expiree && <span className="text-yellow-500">expirée — sans effet</span>}
        </>
      )}
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="px-2 py-1 rounded bg-[#F9004D] hover:bg-[#D00040] text-white disabled:opacity-50"
      >
        {saving ? '...' : 'Enregistrer'}
      </button>
    </div>
  );
}

/* ========== MANAGE HOSTS ========== */
var RELIABILITY_BADGE = {
  flagged: { label: 'Signalé', cls: 'bg-yellow-500/20 text-yellow-400' },
  warning: { label: 'Averti', cls: 'bg-orange-500/20 text-orange-400' },
  suspend_suggested: { label: 'À suspendre', cls: 'bg-red-500/20 text-red-400' }
};

function ManageHosts() {
  var [hosts, setHosts] = React.useState([]);
  var [busy, setBusy] = React.useState(null);

  React.useEffect(function() { fetchHosts(); }, []);

  function fetchHosts() {
    axios.get(API_URL + '/api/admin/hosts', { withCredentials: true })
      .then(function(res) { setHosts(res.data); })
      .catch(function() { toast.error('Erreur de chargement'); });
  }

  function deleteHost(guideId) {
    if (!window.confirm('Supprimer ce guide definitivement ?')) return;
    axios.delete(API_URL + '/api/admin/users/' + guideId, { withCredentials: true })
      .then(function() { toast.success('Guide supprime'); fetchHosts(); })
      .catch(function() { toast.error('Erreur de suppression'); });
  }

  function suspendHost(guideId) {
    var reason = window.prompt('Motif de la suspension (optionnel) :', '');
    if (reason === null) return;
    setBusy(guideId);
    axios.post(API_URL + '/api/admin/hosts/' + guideId + '/suspend', { reason: reason }, { withCredentials: true })
      .then(function(res) { toast.success(res.data.message + ' (' + res.data.deactivated_activities + ' activité(s) désactivée(s))'); fetchHosts(); })
      .catch(function() { toast.error('Erreur'); })
      .finally(function() { setBusy(null); });
  }

  function reactivateHost(guideId) {
    setBusy(guideId);
    axios.post(API_URL + '/api/admin/hosts/' + guideId + '/reactivate', {}, { withCredentials: true })
      .then(function(res) { toast.success(res.data.message); fetchHosts(); })
      .catch(function() { toast.error('Erreur'); })
      .finally(function() { setBusy(null); });
  }

  var hostRows = [];
  for (var i = 0; i < hosts.length; i++) {
    var host = hosts[i];
    var badge = RELIABILITY_BADGE[host.level];
    var rateColor = (host.host_cancel_rate >= 20) ? 'text-red-400' : (host.host_cancel_rate >= 10) ? 'text-yellow-400' : 'text-zinc-400';
    hostRows.push(
      <div key={host.guide_id} className={'flex items-center justify-between p-4 rounded-lg ' + (host.suspended ? 'bg-red-950/30 border border-red-500/30' : 'bg-zinc-800/50')}>
        <div className="flex items-center gap-4 min-w-0">
          <img src={host.profile_photo_url || host.user_picture || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%2327272a'/%3E%3Ccircle cx='50' cy='40' r='16' fill='%2352525b'/%3E%3Cpath d='M22 82c0-15 12-24 28-24s28 9 28 24' fill='%2352525b'/%3E%3C/svg%3E"} alt="" className="h-12 w-12 rounded-full object-cover object-top flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white font-bold">{host.user_name}</span>
              {host.suspended && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">Suspendu</span>}
              {!host.suspended && badge && <span className={'text-xs font-semibold px-2 py-0.5 rounded-full ' + badge.cls}>{badge.label}</span>}
            </div>
            <div className="text-zinc-400 text-sm">{host.areas?.join(', ')}</div>
            <div className="text-xs mt-0.5">
              <span className="text-zinc-500">{host.total_bookings || 0} résa · </span>
              <span className={rateColor}>{host.host_cancelled || 0} annul. guide ({host.host_cancel_rate || 0}%)</span>
              {host.host_cancel_last_minute > 0 && <span className="text-red-500"> · ⚠{host.host_cancel_last_minute} dernière minute</span>}
            </div>
            <CandidatureDocuments app={host} onUpdated={fetchHosts} />
            <FeatureHostControl host={host} onUpdated={fetchHosts} />
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {host.suspended ? (
            <Button onClick={function(id) { return function() { reactivateHost(id); }; }(host.guide_id)} disabled={busy === host.guide_id} size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white">
              <Unlock className="h-4 w-4 mr-2" /> Réactiver
            </Button>
          ) : (
            <Button onClick={function(id) { return function() { suspendHost(id); }; }(host.guide_id)} disabled={busy === host.guide_id} size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
              <Lock className="h-4 w-4 mr-2" /> Suspendre
            </Button>
          )}
          <Button onClick={function(id) { return function() { deleteHost(id); }; }(host.guide_id)} variant="destructive" size="sm">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-8" data-testid="manage-hosts">
      <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Gestion des Guides</h2>
      <p className="text-zinc-500 text-sm mb-6">Suspendre désactive les activités du guide (plus de nouvelles réservations). Les réservations confirmées sont honorées.</p>
      {hosts.length === 0 ? <p className="text-zinc-400 text-center py-8">Aucun guide</p> : <div className="space-y-3">{hostRows}</div>}
    </Card>
  );
}

/* ========== MANAGE TRAVELERS ========== */
function ManageTravelers() {
  var [travelers, setTravelers] = React.useState([]);

  React.useEffect(function() { fetchTravelers(); }, []);

  function fetchTravelers() {
    axios.get(API_URL + '/api/admin/users', { withCredentials: true })
      .then(function(res) {
        var t = [];
        for (var i = 0; i < res.data.length; i++) { if (res.data[i].role === 'traveler') t.push(res.data[i]); }
        setTravelers(t);
      })
      .catch(function() { toast.error('Erreur de chargement'); });
  }

  function deleteUser(userId) {
    if (!window.confirm('Supprimer cet utilisateur definitivement ?')) return;
    axios.delete(API_URL + '/api/admin/users/' + userId, { withCredentials: true })
      .then(function() { toast.success('Utilisateur supprime'); fetchTravelers(); })
      .catch(function() { toast.error('Erreur de suppression'); });
  }

  var travelerRows = [];
  for (var i = 0; i < travelers.length; i++) {
    var user = travelers[i];
    travelerRows.push(
      <div key={user.user_id} className="flex items-center justify-between bg-zinc-800/50 p-4 rounded-lg">
        <div className="flex items-center gap-4">
          <img src={user.picture || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%2327272a'/%3E%3Ccircle cx='50' cy='40' r='16' fill='%2352525b'/%3E%3Cpath d='M22 82c0-15 12-24 28-24s28 9 28 24' fill='%2352525b'/%3E%3C/svg%3E"} alt="" className="h-12 w-12 rounded-full" />
          <div>
            <div className="text-white font-bold">{user.name}</div>
            <div className="text-zinc-400 text-sm">{user.email}</div>
          </div>
        </div>
        <Button onClick={function(id) { return function() { deleteUser(id); }; }(user.user_id)} variant="destructive" size="sm">
          <Trash2 className="h-4 w-4 mr-2" /> Supprimer
        </Button>
      </div>
    );
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-8" data-testid="manage-travelers">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Gestion des Voyageurs</h2>
      {travelers.length === 0 ? <p className="text-zinc-400 text-center py-8">Aucun voyageur</p> : <div className="space-y-3">{travelerRows}</div>}
    </Card>
  );
}

/* ========== MANAGE ADMINS ========== */
function ManageAdmins() {
  var [admins, setAdmins] = React.useState([]);
  var [newAdminEmail, setNewAdminEmail] = React.useState('');
  var [creating, setCreating] = React.useState(false);

  React.useEffect(function() { fetchAdmins(); }, []);

  function fetchAdmins() {
    axios.get(API_URL + '/api/admin/users', { withCredentials: true })
      .then(function(res) {
        var a = [];
        for (var i = 0; i < res.data.length; i++) { if (res.data[i].role === 'admin') a.push(res.data[i]); }
        setAdmins(a);
      })
      .catch(function() { toast.error('Erreur de chargement'); });
  }

  function createAdmin() {
    if (!newAdminEmail || newAdminEmail.indexOf('@') < 0) { toast.error('Email invalide'); return; }
    setCreating(true);
    axios.post(API_URL + '/api/admin/create-admin', { email: newAdminEmail }, { withCredentials: true })
      .then(function() { toast.success('Admin cree !'); setNewAdminEmail(''); fetchAdmins(); setCreating(false); })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Erreur'); setCreating(false); });
  }

  var adminRows = [];
  for (var i = 0; i < admins.length; i++) {
    var admin = admins[i];
    adminRows.push(
      <div key={admin.user_id} className="flex items-center justify-between bg-zinc-800/50 p-4 rounded-lg border border-[#F9004D]/20">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 bg-[#F9004D]/20 rounded-full flex items-center justify-center">
            <Shield className="h-6 w-6 text-[#F9004D]" />
          </div>
          <div>
            <div className="text-white font-bold">{admin.name}</div>
            <div className="text-zinc-400 text-sm">{admin.email}</div>
          </div>
        </div>
        {admin.email === 'bodinaudkevin@gmail.com' && (
          <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-3 py-1 rounded-full">Super Admin</span>
        )}
      </div>
    );
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-8" data-testid="manage-admins">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Gestion des Administrateurs</h2>
      <Card className="bg-zinc-800/50 border-[#F9004D]/30 p-6 mb-8">
        <h3 className="text-lg font-bold text-white mb-4">Creer un Nouvel Admin</h3>
        <div className="flex gap-3">
          <Input type="email" value={newAdminEmail} onChange={function(e) { setNewAdminEmail(e.target.value); }}
            placeholder="email@example.com" className="bg-black/20 border-white/10 text-white" />
          <Button onClick={createAdmin} disabled={creating} className="bg-[#F9004D] hover:bg-[#D00040] text-white px-6">Creer Admin</Button>
        </div>
      </Card>
      {admins.length === 0 ? <p className="text-zinc-400 text-center py-8">Aucun administrateur</p> : <div className="space-y-3">{adminRows}</div>}
    </Card>
  );
}

/* ========== MANAGE BOOKINGS ========== */
function ManageBookings() {
  var [bookings, setBookings] = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [expandedId, setExpandedId] = React.useState(null);

  React.useEffect(function() { fetchBookings(); }, []);

  function fetchBookings() {
    axios.get(API_URL + '/api/admin/bookings', { withCredentials: true })
      .then(function(res) { setBookings(res.data); setLoading(false); })
      .catch(function() { toast.error('Erreur de chargement'); setLoading(false); });
  }

  function cancelBooking(bookingId) {
    if (!window.confirm('Annuler cette reservation ?')) return;
    axios.put(API_URL + '/api/admin/bookings/' + bookingId + '/cancel', {}, { withCredentials: true })
      .then(function() { toast.success('Reservation annulee'); fetchBookings(); })
      .catch(function() { toast.error('Erreur'); });
  }

  if (loading) return <div className="text-white text-center py-8">Chargement...</div>;

  var bookingCards = [];
  for (var i = 0; i < bookings.length; i++) {
    var b = bookings[i];
    var statusMap = { confirmed: 'Confirme', pending: 'En attente', cancelled: 'Annule', modified: 'Modifie' };
    var statusColorMap = { confirmed: 'bg-green-500/20 text-green-400', pending: 'bg-yellow-500/20 text-yellow-400', cancelled: 'bg-red-500/20 text-red-400', modified: 'bg-blue-500/20 text-blue-400' };
    var paymentMap = { paid: 'Paye', unpaid: 'Non paye', failed: 'Echoue' };
    var paymentColorMap = { paid: 'bg-green-500/20 text-green-400', unpaid: 'bg-yellow-500/20 text-yellow-400', failed: 'bg-red-500/20 text-red-400' };

    var statusClass = statusColorMap[b.booking_status] || statusColorMap.pending;
    var statusLabel = statusMap[b.booking_status] || b.booking_status;
    var paymentClass = paymentColorMap[b.payment_status] || paymentColorMap.unpaid;
    var paymentLabel = paymentMap[b.payment_status] || b.payment_status;

    var actNameAdmin = b.activity_name && typeof b.activity_name === 'object' ? (b.activity_name.fr || b.activity_name.en) : (b.activity_name || 'Experience');

    var participantRows = [];
    if (b.participants) {
      for (var j = 0; j < b.participants.length; j++) {
        var p = b.participants[j];
        participantRows.push(
          <div key={j} className="text-sm flex justify-between">
            <span className="text-zinc-300">{p.name}</span>
            <span className={p.rate_type === 'adult' ? 'text-[#00F0FF]' : 'text-[#D4AF37]'}>{formatYen(p.rate_amount)}</span>
          </div>
        );
      }
    }

    var historyItems = [];
    if (b.action_history) {
      for (var h = 0; h < b.action_history.length; h++) {
        var act = b.action_history[h];
        var aLabel = act.action === 'modification' ? 'Modification' : act.action === 'cancellation' ? 'Annulation' : act.action;
        var aColor = act.action === 'cancellation' ? 'text-red-400' : 'text-blue-400';
        historyItems.push(
          <div key={h} className="text-sm py-1 border-b border-white/5 last:border-0">
            <div className="flex justify-between">
              <span className={aColor + ' font-bold'}>{aLabel}</span>
              <span className="text-zinc-500 text-xs">{act.timestamp ? new Date(act.timestamp).toLocaleString('fr-FR') : ''}</span>
            </div>
            {act.modification_fee > 0 && <span className="text-yellow-400 text-xs">Frais: {formatYen(act.modification_fee)}</span>}
            {act.cancellation_fee > 0 && <span className="text-red-400 text-xs ml-2">Frais: {formatYen(act.cancellation_fee)}</span>}
            {act.refund_amount > 0 && <span className="text-green-400 text-xs ml-2">Remb: {formatYen(act.refund_amount)}</span>}
            {act.details && <p className="text-zinc-500 text-xs">{act.details}</p>}
          </div>
        );
      }
    }

    var isExpanded = expandedId === b.booking_id;

    bookingCards.push(
      <Card key={b.booking_id} className="bg-zinc-800/50 border-white/10 overflow-hidden" data-testid={'admin-booking-' + b.booking_id}>
        <div className="p-6">
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div className="flex-1 min-w-[250px]">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={'px-2 py-1 rounded text-xs ' + statusClass}>{statusLabel}</span>
                <span className={'px-2 py-1 rounded text-xs ' + paymentClass}>{paymentLabel}</span>
                {b.total_fees_paid > 0 && <span className="px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400">Frais: {formatYen(b.total_fees_paid)}</span>}
                {b.refund_amount > 0 && <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">Rembourse: {formatYen(b.refund_amount)}</span>}
              </div>
              <h4 className="text-lg font-bold text-white mb-1">{actNameAdmin}</h4>
              <p className="text-zinc-400 text-sm">Guide: {b.guide_name || 'N/A'}</p>
              <p className="text-zinc-400 text-sm">Client: {b.traveler_name} ({b.traveler_email})</p>
              <p className="text-zinc-400 text-sm mt-2"><Calendar className="inline h-4 w-4 mr-1" />{b.start_date} - {b.number_of_days} jour(s)</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-[#F9004D] mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatYen(b.total_price)}</div>
              <p className="text-xs text-zinc-500 mb-3">ID: {b.booking_id?.slice(-12)}</p>
              <div className="flex flex-col gap-2">
                <Button onClick={function(id) { return function() { setExpandedId(expandedId === id ? null : id); }; }(b.booking_id)} variant="outline" size="sm" className="border-white/20 text-zinc-300">
                  <Eye className="h-4 w-4 mr-1" /> {isExpanded ? 'Masquer' : 'Details'}
                </Button>
                {b.booking_status !== 'cancelled' && (
                  <Button onClick={function(id) { return function() { cancelBooking(id); }; }(b.booking_id)} variant="outline" size="sm" className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <XCircle className="h-4 w-4 mr-1" /> Annuler
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="border-t border-white/10 p-6 space-y-4">
            <div>
              <p className="text-zinc-400 text-sm mb-2 font-bold">Participants ({b.participants?.length || 0}):</p>
              <div className="space-y-1">{participantRows}</div>
            </div>
            {(b.modification_fee > 0 || b.cancellation_fee > 0 || b.refund_amount > 0) && (
              <div className="bg-zinc-900/50 p-4 rounded-lg">
                <p className="text-zinc-400 text-sm mb-2 font-bold">Finances</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-400">Montant initial</span><span className="text-white">{formatYen(b.total_price)}</span></div>
                  {b.modification_fee > 0 && <div className="flex justify-between"><span className="text-yellow-400">Frais modification</span><span className="text-yellow-400">{formatYen(b.modification_fee)}</span></div>}
                  {b.cancellation_fee > 0 && <div className="flex justify-between"><span className="text-red-400">Frais annulation</span><span className="text-red-400">{formatYen(b.cancellation_fee)}</span></div>}
                  {b.refund_amount > 0 && <div className="flex justify-between"><span className="text-green-400">Montant rembourse</span><span className="text-green-400">{formatYen(b.refund_amount)}</span></div>}
                  {b.total_fees_paid > 0 && <div className="flex justify-between border-t border-white/10 pt-1"><span className="text-zinc-300 font-bold">Total frais</span><span className="text-zinc-300 font-bold">{formatYen(b.total_fees_paid)}</span></div>}
                </div>
              </div>
            )}
            {historyItems.length > 0 && (
              <div>
                <p className="text-zinc-400 text-sm mb-2 font-bold">Historique des actions</p>
                <div className="bg-zinc-900/50 p-4 rounded-lg">{historyItems}</div>
              </div>
            )}
            <div className="text-xs text-zinc-600">
              Cree: {b.created_at ? new Date(b.created_at).toLocaleString('fr-FR') : 'N/A'}
              {b.cancelled_at && (' | Annule: ' + new Date(b.cancelled_at).toLocaleString('fr-FR'))}
              {b.updated_at && (' | Modifie: ' + new Date(b.updated_at).toLocaleString('fr-FR'))}
            </div>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-8" data-testid="manage-bookings">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Gestion des Reservations ({bookings.length})</h2>
      {bookings.length === 0 ? <p className="text-zinc-400 text-center py-12">Aucune reservation</p> : <div className="space-y-4">{bookingCards}</div>}
    </Card>
  );
}

/* ========== ACTIVITY PHOTOS MANAGER (ADMIN) ========== */
function ActivityPhotosManager() {
  var [activities, setActivities] = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [uploadingId, setUploadingId] = React.useState(null);

  React.useEffect(function() { fetchActivities(); }, []);

  function fetchActivities() {
    axios.get(API_URL + '/api/admin/activities/all', { withCredentials: true })
      .then(function(res) { setActivities(res.data); setLoading(false); })
      .catch(function() { toast.error('Erreur de chargement'); setLoading(false); });
  }

  function savePhotos(activityId, photos) {
    return axios.put(API_URL + '/api/admin/activities/' + activityId + '/photos', { photos: photos }, { withCredentials: true });
  }

  function handleUpload(activity, e) {
    var file = e.target.files[0];
    if (!file) return;
    var current = activity.photos || [];
    if (current.length >= 5) { toast.error('Maximum 5 photos'); return; }
    setUploadingId(activity.activity_id);
    uploadFile(file)
      .then(function(url) {
        var next = current.concat([url]);
        return savePhotos(activity.activity_id, next);
      })
      .then(function() { toast.success('Photo ajoutee'); setUploadingId(null); fetchActivities(); })
      .catch(function() { toast.error("Echec de l'upload"); setUploadingId(null); });
    e.target.value = '';
  }

  function handleRemove(activity, idx) {
    var current = activity.photos || [];
    var next = [];
    for (var i = 0; i < current.length; i++) { if (i !== idx) next.push(current[i]); }
    savePhotos(activity.activity_id, next)
      .then(function() { toast.success('Photo retiree'); fetchActivities(); })
      .catch(function() { toast.error('Erreur'); });
  }

  if (loading) return <div className="text-white text-center py-8">Chargement...</div>;

  var cards = [];
  for (var i = 0; i < activities.length; i++) {
    var act = activities[i];
    var actName = act.name && typeof act.name === 'object' ? (act.name.fr || act.name.en) : (act.name || 'Activite');
    var photos = act.photos || [];
    var thumbs = [];
    for (var j = 0; j < photos.length; j++) {
      thumbs.push(
        <div key={photos[j] + j} className="relative w-24 h-24 rounded-lg overflow-hidden border border-white/10" data-testid={'admin-activity-photo-' + i + '-' + j}>
          <img src={photos[j]} alt="" className="w-full h-full object-cover"
            onError={function(e) { e.target.src = 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&q=80'; }} />
          <button type="button" onClick={(function(a, idx) { return function() { handleRemove(a, idx); }; })(act, j)}
            className="absolute top-1 right-1 bg-black/70 rounded-full p-1 text-red-400 hover:text-red-300">
            <X className="h-3 w-3" />
          </button>
        </div>
      );
    }
    var isUploading = uploadingId === act.activity_id;
    cards.push(
      <Card key={act.activity_id} className="bg-zinc-800/50 border-white/10 p-5" data-testid={'admin-photo-activity-' + act.activity_id}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <span className="text-[#F9004D] text-sm font-bold">{act.location}</span>
            <h4 className="text-white font-bold">{actName}</h4>
            <span className="text-zinc-500 text-xs">Guide: {act.guide_name} | {photos.length} photo(s)</span>
          </div>
          <span className={'px-2 py-1 rounded text-xs ' + (act.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400')}>{act.status}</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {thumbs}
          {photos.length < 5 && (
            <label className="w-24 h-24 rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-[#F9004D]/50 text-zinc-400" data-testid={'admin-add-photo-' + act.activity_id}>
              {isUploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImageIcon className="h-6 w-6" />}
              <span className="text-xs mt-1">{isUploading ? '...' : 'Ajouter'}</span>
              <input type="file" accept="image/*" onChange={(function(a) { return function(e) { handleUpload(a, e); }; })(act)} className="hidden" disabled={isUploading} />
            </label>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-8" data-testid="activity-photos-manager">
      <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Photos des Activites</h2>
      <p className="text-zinc-400 mb-6 text-sm">Ajoutez ou retirez les photos de chaque activite (visible par les voyageurs). Reserve a l'administrateur.</p>
      {activities.length === 0 ? <p className="text-zinc-400 text-center py-12">Aucune activite</p> : <div className="space-y-4">{cards}</div>}
    </Card>
  );
}

/* ========== VIREMENTS / PAYOUTS ========== */
function AdminPayouts() {
  var cfg = useConfig();
  var [payouts, setPayouts] = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [processing, setProcessing] = React.useState(false);
  var [refundDialog, setRefundDialog] = React.useState(null); // { bookingId, totalPrice, alreadyRefunded, guideName }
  var [refundAmount, setRefundAmount] = React.useState('');
  var [refundReason, setRefundReason] = React.useState('');
  var [refunding, setRefunding] = React.useState(false);
  var API_URL = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(function() { fetchPayouts(); }, []);

  function fetchPayouts() {
    setLoading(true);
    axios.get(API_URL + '/api/admin/payouts', { withCredentials: true })
      .then(function(r) { setPayouts(r.data); })
      .catch(function() { toast.error('Erreur chargement virements'); })
      .finally(function() { setLoading(false); });
  }

  function processAll() {
    setProcessing(true);
    axios.post(API_URL + '/api/admin/process-payouts', {}, { withCredentials: true })
      .then(function(r) { toast.success(r.data.message); fetchPayouts(); })
      .catch(function() { toast.error('Erreur'); })
      .finally(function() { setProcessing(false); });
  }

  function release(bookingId) {
    axios.post(API_URL + '/api/admin/payouts/' + bookingId + '/release', {}, { withCredentials: true })
      .then(function(r) { toast.success(r.data.message); fetchPayouts(); })
      .catch(function() { toast.error('Erreur'); });
  }

  function freeze(bookingId) {
    var reason = window.prompt('Motif du gel (visible dans la liste) :', '');
    if (reason === null) return; // annulé
    axios.post(API_URL + '/api/admin/payouts/' + bookingId + '/freeze', { reason: reason }, { withCredentials: true })
      .then(function(r) { toast.success(r.data.message); fetchPayouts(); })
      .catch(function(e) { toast.error(e.response?.data?.detail || 'Erreur gel'); });
  }

  function unfreeze(bookingId) {
    axios.post(API_URL + '/api/admin/payouts/' + bookingId + '/unfreeze', {}, { withCredentials: true })
      .then(function(r) { toast.success(r.data.message); fetchPayouts(); })
      .catch(function(e) { toast.error(e.response?.data?.detail || 'Erreur'); });
  }

  function openRefundDialog(p) {
    var alreadyRefunded = p.refund_amount || 0;
    setRefundDialog({ bookingId: p.booking_id, totalPrice: p.total_price || 0, alreadyRefunded: alreadyRefunded, guideName: p.guide_name });
    setRefundAmount(String((p.total_price || 0) - alreadyRefunded));
    setRefundReason('');
  }

  function submitRefund() {
    if (!refundDialog) return;
    var amt = parseInt(refundAmount, 10);
    if (isNaN(amt) || amt <= 0) { toast.error('Montant invalide'); return; }
    setRefunding(true);
    axios.post(API_URL + '/api/admin/bookings/' + refundDialog.bookingId + '/refund', { refund_amount: amt, reason: refundReason }, { withCredentials: true })
      .then(function(r) { toast.success(r.data.message); setRefundDialog(null); fetchPayouts(); })
      .catch(function(e) { toast.error(e.response?.data?.detail || 'Erreur remboursement'); })
      .finally(function() { setRefunding(false); });
  }

  var statusColor = { pending: '#F59E0B', released: '#10B981', disputed: '#EF4444' };
  var statusLabel = { pending: 'En attente', released: 'Viré', disputed: 'Réclamation' };
  var statusIcon = { pending: Clock, released: CheckCircle, disputed: AlertTriangle };

  var totalPending = payouts.filter(function(p) { return p.payout_status === 'pending'; }).reduce(function(s, p) { return s + (p.guide_amount || 0); }, 0);
  var totalReleased = payouts.filter(function(p) { return p.payout_status === 'released'; }).reduce(function(s, p) { return s + (p.guide_amount || 0); }, 0);
  var totalPlatform = payouts.filter(function(p) { return p.payout_status === 'released'; }).reduce(function(s, p) { return s + (p.platform_amount || 0); }, 0);

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-white/10 p-5">
          <div className="text-zinc-400 text-sm mb-1">En séquestre</div>
          <div className="text-2xl font-bold text-[#F59E0B]">{totalPending.toLocaleString()} ¥</div>
          <div className="text-xs text-zinc-500 mt-1">virements en attente</div>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10 p-5">
          <div className="text-zinc-400 text-sm mb-1">Virés aux guides</div>
          <div className="text-2xl font-bold text-[#10B981]">{totalReleased.toLocaleString()} ¥</div>
          <div className="text-xs text-zinc-500 mt-1">{cfg.guide_fee_percent}% du tarif</div>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10 p-5">
          <div className="text-zinc-400 text-sm mb-1">Commission plateforme</div>
          <div className="text-2xl font-bold text-[#F9004D]">{totalPlatform.toLocaleString()} ¥</div>
          <div className="text-xs text-zinc-500 mt-1">{cfg.platform_fee_percent}% du tarif</div>
        </Card>
      </div>

      <Card className="bg-zinc-900/50 border-white/10 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Virements</h2>
            <p className="text-zinc-400 text-sm mt-1">Séquestre {cfg.payout_hold_days} jours · {cfg.guide_fee_percent}% guide / {cfg.platform_fee_percent}% plateforme · frais Stripe payés par le voyageur</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={fetchPayouts} variant="outline" className="border-white/20 text-zinc-300 hover:text-white text-sm">Actualiser</Button>
            <Button onClick={processAll} disabled={processing} className="bg-[#10B981] hover:bg-[#059669] text-white text-sm">
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4 mr-2" />}
              Traiter les virements dus
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-zinc-500 mx-auto" /></div>
        ) : payouts.length === 0 ? (
          <p className="text-zinc-400 text-center py-12">Aucun paiement enregistré</p>
        ) : (
          <div className="space-y-3">
            {payouts.map(function(p) {
              var StatusIcon = statusIcon[p.payout_status] || Clock;
              var color = statusColor[p.payout_status] || '#6B7280';
              var label = statusLabel[p.payout_status] || p.payout_status;
              var eligibleDate = p.payout_eligible_date ? new Date(p.payout_eligible_date).toLocaleDateString('fr-FR') : '—';
              return (
                <div key={p.booking_id} className="flex items-center gap-4 p-4 rounded-xl bg-zinc-800/50 border border-white/5">
                  <StatusIcon className="h-5 w-5 flex-shrink-0" style={{ color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-white font-semibold text-sm truncate">{p.guide_name}</span>
                      <span className="text-xs text-zinc-500">· {p.traveler_name}</span>
                      {p.is_cancellation_payout && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B]" title={'Indemnité d\'annulation tardive (' + cfg.guide_fee_percent + '% des frais retenus)'}>
                          annulation
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-400">
                      Activité : {p.start_date ? new Date(p.start_date).toLocaleDateString('fr-FR') : '—'}
                      &nbsp;·&nbsp;Déblocage : {eligibleDate}
                    </div>
                    {p.freeze_reason && (
                      <div className="text-xs text-red-400 mt-1">
                        🔒 Gelé{p.disputed_at ? ' le ' + new Date(p.disputed_at).toLocaleDateString('fr-FR') : ''} — motif : {p.freeze_reason}
                      </div>
                    )}
                    {(p.refunds || []).map(function(r, ri) {
                      return (
                        <div key={ri} className="text-xs text-[#F9004D] mt-1">
                          ↩ Remboursé {(r.amount || 0).toLocaleString()} ¥{r.at ? ' le ' + new Date(r.at).toLocaleDateString('fr-FR') : ''}{r.reason ? ' — motif : ' + r.reason : ''}
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-bold text-white">{(p.guide_amount || 0).toLocaleString()} ¥</div>
                    <div className="text-xs text-zinc-500">+{(p.platform_amount || 0).toLocaleString()} ¥ commission</div>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ color, background: color + '20' }}>{label}</span>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {p.payout_status !== 'released' && (p.is_eligible || (p.is_cancellation_payout && p.payout_status === 'pending')) && (
                      <Button onClick={function(id) { return function() { release(id); }; }(p.booking_id)}
                        title={p.is_cancellation_payout && !p.is_eligible ? "Indemnité d'annulation : l'activité n'aura pas lieu, virement immédiat possible" : ''}
                        className="bg-[#10B981] hover:bg-[#059669] text-white text-xs py-1 px-3 h-auto">
                        <Unlock className="h-3 w-3 mr-1" /> Virer
                      </Button>
                    )}
                    {p.payout_status !== 'released' && p.payout_status !== 'disputed' && (
                      <Button onClick={function(id) { return function() { freeze(id); }; }(p.booking_id)} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs py-1 px-3 h-auto">
                        <Lock className="h-3 w-3 mr-1" /> Geler
                      </Button>
                    )}
                    {p.payout_status === 'disputed' && (p.refund_amount || 0) < (p.total_price || 0) && (
                      <Button onClick={function(id) { return function() { unfreeze(id); }; }(p.booking_id)} variant="outline" className="border-[#10B981]/50 text-[#10B981] hover:bg-[#10B981]/10 text-xs py-1 px-3 h-auto" data-testid={'unfreeze-' + p.booking_id}>
                        <Unlock className="h-3 w-3 mr-1" /> Débloquer
                      </Button>
                    )}
                    {p.payment_status === 'paid' && (p.refund_amount || 0) < (p.total_price || 0) && (
                      <Button onClick={function(payout) { return function() { openRefundDialog(payout); }; }(p)} variant="outline" className="border-[#F9004D]/50 text-[#F9004D] hover:bg-[#F9004D]/10 text-xs py-1 px-3 h-auto">
                        <RefreshCw className="h-3 w-3 mr-1" /> Rembourser
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Refund Dialog */}
      {refundDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={function() { setRefundDialog(null); }}>
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl" onClick={function(e) { e.stopPropagation(); }}>
            <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Remboursement</h3>
            <p className="text-zinc-400 text-sm mb-6">Réservation de <strong className="text-white">{refundDialog.guideName}</strong></p>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-xs text-zinc-500 mb-1">
                  <span>Montant total payé</span>
                  <span>{refundDialog.totalPrice.toLocaleString()} ¥</span>
                </div>
                {refundDialog.alreadyRefunded > 0 && (
                  <div className="flex justify-between text-xs text-zinc-500 mb-1">
                    <span>Déjà remboursé</span>
                    <span className="text-[#F9004D]">−{refundDialog.alreadyRefunded.toLocaleString()} ¥</span>
                  </div>
                )}
                <div className="flex justify-between text-xs text-zinc-400 mb-3">
                  <span>Remboursable max</span>
                  <span className="text-white font-semibold">{(refundDialog.totalPrice - refundDialog.alreadyRefunded).toLocaleString()} ¥</span>
                </div>
              </div>

              <div>
                <label className="text-zinc-300 text-sm mb-1 block">Montant à rembourser (¥)</label>
                <div className="flex gap-2">
                  <Input type="number" value={refundAmount} onChange={function(e) { setRefundAmount(e.target.value); }}
                    className="bg-zinc-800 border-white/10 text-white flex-1" placeholder="Montant en ¥" min="1" max={refundDialog.totalPrice - refundDialog.alreadyRefunded} />
                  <Button variant="outline" className="border-white/20 text-zinc-300 text-xs" onClick={function() { setRefundAmount(String(refundDialog.totalPrice - refundDialog.alreadyRefunded)); }}>
                    Total
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-zinc-300 text-sm mb-1 block">Motif (optionnel)</label>
                <Input value={refundReason} onChange={function(e) { setRefundReason(e.target.value); }}
                  className="bg-zinc-800 border-white/10 text-white" placeholder="Ex: Activité annulée par le guide" />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={function() { setRefundDialog(null); }} variant="outline" className="flex-1 border-white/20 text-zinc-300">
                Annuler
              </Button>
              <Button onClick={submitRefund} disabled={refunding} className="flex-1 bg-[#F9004D] hover:bg-[#D00040] text-white">
                {refunding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Confirmer le remboursement
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== CODES PROMO ========== */
function ManagePromoCodes() {
  var API_URL = process.env.REACT_APP_BACKEND_URL;
  var cfg = useConfig();
  // Plafond des remises : imposé par le serveur (business.MAX_PROMO_PERCENT).
  // Au-delà, la remise serait prélevée sur la part du guide.
  var maxPct = (cfg && cfg.max_promo_percent) || 12;
  var [codes, setCodes] = React.useState([]);
  var [welcome, setWelcome] = React.useState(null);
  var [form, setForm] = React.useState({ code: '', discount_percent: 10, valid_from: '', valid_until: '', max_uses: '', per_user_once: true });
  var [saving, setSaving] = React.useState(false);

  React.useEffect(function() { fetchAll(); }, []);

  function fetchAll() {
    axios.get(API_URL + '/api/admin/promo-codes', { withCredentials: true }).then(function(r) { setCodes(r.data); }).catch(function() {});
    axios.get(API_URL + '/api/admin/welcome-promo', { withCredentials: true }).then(function(r) { setWelcome(r.data); }).catch(function() {});
  }

  function createCode() {
    if (!form.code.trim()) { toast.error('Code requis'); return; }
    var pct = parseInt(form.discount_percent, 10) || 0;
    if (pct < 1 || pct > maxPct) {
      toast.error('La réduction doit être entre 1 et ' + maxPct + '% : au-delà, elle serait prélevée sur la part de l\'guide.');
      return;
    }
    setSaving(true);
    var payload = {
      code: form.code, discount_percent: parseInt(form.discount_percent, 10) || 0,
      valid_from: form.valid_from || null, valid_until: form.valid_until || null,
      max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null, per_user_once: form.per_user_once, active: true
    };
    axios.post(API_URL + '/api/admin/promo-codes', payload, { withCredentials: true })
      .then(function(r) { toast.success(r.data.message); setForm({ code: '', discount_percent: 10, valid_from: '', valid_until: '', max_uses: '', per_user_once: true }); fetchAll(); })
      .catch(function(e) { toast.error(e.response?.data?.detail || 'Erreur'); })
      .finally(function() { setSaving(false); });
  }

  function toggleCode(code) {
    axios.post(API_URL + '/api/admin/promo-codes/' + code + '/toggle', {}, { withCredentials: true })
      .then(function() { fetchAll(); }).catch(function() { toast.error('Erreur'); });
  }
  function deleteCode(code) {
    if (!window.confirm('Supprimer le code ' + code + ' ?')) return;
    axios.delete(API_URL + '/api/admin/promo-codes/' + code, { withCredentials: true })
      .then(function() { toast.success('Code supprimé'); fetchAll(); }).catch(function() { toast.error('Erreur'); });
  }
  function saveWelcome() {
    var wpct = parseInt(welcome.discount_percent, 10) || 0;
    if (wpct < 1 || wpct > maxPct) {
      toast.error('La réduction doit être entre 1 et ' + maxPct + '%.');
      return;
    }
    axios.put(API_URL + '/api/admin/welcome-promo', {
      code: welcome.code, discount_percent: parseInt(welcome.discount_percent, 10) || 0,
      cap: parseInt(welcome.cap, 10) || 0, active: welcome.active
    }, { withCredentials: true }).then(function() { toast.success('Campagne mise à jour'); fetchAll(); }).catch(function() { toast.error('Erreur'); });
  }

  var rows = [];
  for (var i = 0; i < codes.length; i++) {
    var c = codes[i];
    rows.push(
      <div key={c.code} className={'flex items-center justify-between p-4 rounded-lg ' + (c.active ? 'bg-zinc-800/50' : 'bg-zinc-900/50 opacity-60')}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-bold font-mono">{c.code}</span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#F9004D]/20 text-[#F9004D]">−{c.discount_percent}%</span>
            {!c.active && <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-600/40 text-zinc-400">inactif</span>}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {(c.used_count || 0) + (c.max_uses ? ' / ' + c.max_uses : '') + ' utilisation(s)'}
            {c.valid_from && (' · dès ' + c.valid_from)}
            {c.valid_until && (' · jusqu\'au ' + c.valid_until)}
            {c.per_user_once && ' · 1×/client'}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button onClick={(function(cd) { return function() { toggleCode(cd); }; })(c.code)} size="sm" variant="outline" className="border-white/20 text-zinc-300 text-xs">
            {c.active ? 'Désactiver' : 'Activer'}
          </Button>
          <Button onClick={(function(cd) { return function() { deleteCode(cd); }; })(c.code)} size="sm" variant="destructive"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="manage-promo">
      {/* Welcome campaign */}
      {welcome && (
        <Card className="bg-zinc-900/50 border-[#F9004D]/30 p-6">
          <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2"><Gift className="h-5 w-5 text-[#F9004D]" /> Campagne de bienvenue (X premiers clients)</h3>
          <p className="text-zinc-500 text-sm mb-4">Envoie automatiquement un code après la 1re réservation. Envoyés : {welcome.sent || 0} / {welcome.cap || 0}.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <div><label className="text-zinc-400 text-xs">Code</label><Input value={welcome.code || ''} onChange={function(e) { setWelcome(Object.assign({}, welcome, { code: e.target.value.toUpperCase() })); }} className="bg-zinc-800 border-white/10 text-white" /></div>
            <div>
              <label className="text-zinc-400 text-xs">Réduction % <span className="text-zinc-500">(max {maxPct})</span></label>
              <Input type="number" min="1" max={maxPct} value={welcome.discount_percent || 0} onChange={function(e) { setWelcome(Object.assign({}, welcome, { discount_percent: e.target.value })); }} className="bg-zinc-800 border-white/10 text-white" />
            </div>
            <div><label className="text-zinc-400 text-xs">Plafond</label><Input type="number" value={welcome.cap || 0} onChange={function(e) { setWelcome(Object.assign({}, welcome, { cap: e.target.value })); }} className="bg-zinc-800 border-white/10 text-white" /></div>
            <div className="flex gap-2">
              <Button onClick={function() { setWelcome(Object.assign({}, welcome, { active: !welcome.active })); }} variant="outline" className={'text-xs ' + (welcome.active ? 'border-green-500/50 text-green-400' : 'border-zinc-600 text-zinc-400')}>{welcome.active ? 'Active' : 'Inactive'}</Button>
              <Button onClick={saveWelcome} className="bg-[#F9004D] hover:bg-[#D00040] text-white text-xs">Enregistrer</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Create code */}
      <Card className="bg-zinc-900/50 border-white/10 p-6">
        <h3 className="text-lg font-bold text-white mb-4">Nouveau code promo</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div><label className="text-zinc-400 text-xs">Code</label><Input value={form.code} onChange={function(e) { setForm(Object.assign({}, form, { code: e.target.value.toUpperCase() })); }} placeholder="ETE2026" className="bg-zinc-800 border-white/10 text-white" /></div>
          <div>
            <label className="text-zinc-400 text-xs">Réduction % <span className="text-zinc-500">(max {maxPct})</span></label>
            <Input type="number" min="1" max={maxPct} value={form.discount_percent} onChange={function(e) { setForm(Object.assign({}, form, { discount_percent: e.target.value })); }} className="bg-zinc-800 border-white/10 text-white" />
          </div>
          <div><label className="text-zinc-400 text-xs">Début (opt.)</label><Input type="date" value={form.valid_from} onChange={function(e) { setForm(Object.assign({}, form, { valid_from: e.target.value })); }} className="bg-zinc-800 border-white/10 text-white" /></div>
          <div><label className="text-zinc-400 text-xs">Fin (opt.)</label><Input type="date" value={form.valid_until} onChange={function(e) { setForm(Object.assign({}, form, { valid_until: e.target.value })); }} className="bg-zinc-800 border-white/10 text-white" /></div>
          <div><label className="text-zinc-400 text-xs">Max usages (opt.)</label><Input type="number" value={form.max_uses} onChange={function(e) { setForm(Object.assign({}, form, { max_uses: e.target.value })); }} className="bg-zinc-800 border-white/10 text-white" /></div>
          <Button onClick={createCode} disabled={saving} className="bg-[#F9004D] hover:bg-[#D00040] text-white">Créer</Button>
        </div>
        <label className="flex items-center gap-2 mt-3 text-zinc-400 text-sm">
          <input type="checkbox" checked={form.per_user_once} onChange={function() { setForm(Object.assign({}, form, { per_user_once: !form.per_user_once })); }} className="accent-[#F9004D]" />
          Utilisable une seule fois par client
        </label>
      </Card>

      {/* List */}
      <Card className="bg-zinc-900/50 border-white/10 p-6">
        <h3 className="text-lg font-bold text-white mb-4">Codes existants ({codes.length})</h3>
        {codes.length === 0 ? <p className="text-zinc-500 text-center py-6">Aucun code</p> : <div className="space-y-3">{rows}</div>}
      </Card>
    </div>
  );
}

/* ========== VIDEO MODERATION ========== */
function VideoModeration() {
  var [status, setStatus] = React.useState('pending');
  var [videos, setVideos] = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [busyId, setBusyId] = React.useState(null);

  function load(s) {
    setLoading(true);
    axios.get(API_URL + '/api/admin/videos?status=' + s, { withCredentials: true })
      .then(function(r) { setVideos(r.data.videos || []); })
      .catch(function() { setVideos([]); })
      .finally(function() { setLoading(false); });
  }

  React.useEffect(function() { load(status); }, [status]);

  function moderate(v, action) {
    var reason = null;
    if (action === 'reject') { reason = window.prompt('Raison du refus (optionnel) :') || ''; }
    setBusyId(v.video_id);
    axios.post(API_URL + '/api/admin/videos/' + v.video_id + '/moderate', { action: action, reason: reason }, { withCredentials: true })
      .then(function() {
        toast.success(action === 'approve' ? 'Vidéo approuvée' : 'Vidéo refusée');
        setVideos(function(list) { return list.filter(function(x) { return x.video_id !== v.video_id; }); });
      })
      .catch(function() { toast.error('Action impossible'); })
      .finally(function() { setBusyId(null); });
  }

  var filters = [['pending', 'En attente'], ['approved', 'Approuvées'], ['rejected', 'Refusées'], ['all', 'Toutes']];

  return (
    <div data-testid="admin-videos">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Modération des vidéos</h2>
      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map(function(f) {
          return (
            <Button key={f[0]} onClick={function() { setStatus(f[0]); }} size="sm"
              variant={status === f[0] ? 'default' : 'outline'}
              className={status === f[0] ? 'bg-[#F9004D] text-white' : 'border-white/20 text-zinc-300 hover:bg-white/10'}
              data-testid={'video-filter-' + f[0]}>
              {f[1]}
            </Button>
          );
        })}
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" /></div>
      ) : videos.length === 0 ? (
        <p className="text-zinc-500 text-center py-12">Aucune vidéo</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map(function(v) {
            return (
              <Card key={v.video_id} className="bg-zinc-900/50 border-white/10 p-4" data-testid={'admin-video-' + v.video_id}>
                <div className="rounded-xl overflow-hidden bg-black mb-3" style={{ aspectRatio: '9 / 16' }}>
                  <video src={API_URL + v.file_url} poster={API_URL + v.poster_url} controls playsInline className="w-full h-full object-cover" />
                </div>
                <p className="text-white text-sm font-bold truncate">{v.activity_name}</p>
                <p className="text-zinc-400 text-xs mb-3">
                  {(v.uploader_role === 'host' ? 'Guide' : (v.author_label || 'Voyageur'))}
                  {v.created_at ? ' · ' + new Date(v.created_at).toLocaleDateString('fr-FR') : ''}
                </p>
                {v.status === 'pending' ? (
                  <div className="flex gap-2">
                    <Button onClick={function() { moderate(v, 'approve'); }} disabled={busyId === v.video_id} size="sm" className="flex-1 bg-green-500/20 text-green-400 hover:bg-green-500/30" data-testid={'approve-' + v.video_id}>
                      <CheckCircle className="h-4 w-4 mr-1" /> Approuver
                    </Button>
                    <Button onClick={function() { moderate(v, 'reject'); }} disabled={busyId === v.video_id} size="sm" variant="outline" className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={'reject-' + v.video_id}>
                      <XCircle className="h-4 w-4 mr-1" /> Refuser
                    </Button>
                  </div>
                ) : (
                  <span className={'inline-block px-3 py-1 rounded-full text-xs ' + (v.status === 'approved' ? 'bg-green-500/20 text-green-400' : v.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-zinc-400')}>
                    {v.status}
                  </span>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ========== MAIN ADMIN DASHBOARD ========== */
/* ========== ÉDITION DES ACTIVITÉS (ADMIN) ========== */
// L'admin peut modifier n'importe quelle activité (le backend applique les
// changements immédiatement pour un admin, sans passer par la validation).
function AdminActivityEditor() {
  var [activities, setActivities] = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [editing, setEditing] = React.useState(null);

  function fetchAll() {
    setLoading(true);
    axios.get(API_URL + '/api/admin/activities/all', { withCredentials: true })
      .then(function(res) { setActivities(res.data || []); setLoading(false); })
      .catch(function() { setLoading(false); });
  }
  React.useEffect(function() { fetchAll(); }, []);

  function slotFromStart(hhmm) {
    var h = parseInt((hhmm || '09:00').split(':')[0], 10);
    if (isNaN(h)) return [];
    return h < 12 ? ['morning'] : (h < 18 ? ['afternoon'] : ['evening']);
  }

  function handleSave(form, done) {
    var id = editing.activity_id;
    var payload = {
      meeting_point: form.meeting_point,
      meeting_point_url: form.meeting_point_url,
      start_time: form.start_time,
      duration_hours: form.duration_hours,
      name_fr: form.name_fr, name_en: form.name_en, name_jp: form.name_jp,
      description_fr: form.description_fr, description_en: form.description_en, description_jp: form.description_jp,
      adult_rate: form.adult_rate,
      child_rate: form.child_rate,
      children_allowed: form.children_allowed,
      max_participants: form.max_participants,
      group_discount_enabled: form.group_discount_enabled,
      group_discounts: form.group_discounts,
      included: form.included || [],
      not_included: form.not_included || []
    };
    axios.put(API_URL + '/api/guides/me/activities/' + id, payload, { withCredentials: true })
      .then(function() {
        if (form.photos) {
          axios.put(API_URL + '/api/guides/me/activities/' + id + '/photos', { photos: form.photos }, { withCredentials: true }).catch(function() {});
        }
        axios.put(API_URL + '/api/guides/me/activities/' + id + '/availability', {
          days_of_week: form.days_of_week,
          time_slots: slotFromStart(form.start_time),
          start_date: form.start_date || null,
          end_date: form.end_date || null
        }, { withCredentials: true }).catch(function() {});
        toast.success('Activité mise à jour');
        setEditing(null);
        fetchAll();
        done();
      })
      .catch(function(err) {
        toast.error((err.response && err.response.data && err.response.data.detail) || 'Erreur lors de la modification');
        done();
      });
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" /></div>;
  }

  if (editing) {
    return (
      <div data-testid="admin-activity-editor">
        <Button variant="outline" onClick={function() { setEditing(null); }} className="mb-4 border-white/20 text-zinc-300">
          ← Retour à la liste
        </Button>
        <ActivityForm activity={editing} onSave={handleSave} onCancel={function() { setEditing(null); }} />
      </div>
    );
  }

  var rows = [];
  for (var i = 0; i < activities.length; i++) {
    (function(a) {
      var nm = a.name && typeof a.name === 'object' ? (a.name.fr || a.name.en || a.name.jp) : a.name;
      rows.push(
        <Card key={a.activity_id} className="bg-zinc-800/50 border-white/10 p-4 flex items-center justify-between gap-4" data-testid={'admin-edit-activity-' + a.activity_id}>
          <div className="min-w-0">
            <p className="text-white font-bold truncate">{nm || '(sans titre)'}</p>
            <p className="text-zinc-500 text-sm truncate">{a.location} · {a.guide_name || ''} · {a.status}</p>
          </div>
          <Button onClick={function() { setEditing(a); }} className="bg-[#F9004D] hover:bg-[#D00040] text-white shrink-0" data-testid={'admin-edit-btn-' + a.activity_id}>
            <Pencil className="h-4 w-4 mr-1" /> Modifier
          </Button>
        </Card>
      );
    })(activities[i]);
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-8" data-testid="admin-activities-editor">
      <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Modifier les activités</h2>
      <p className="text-zinc-500 text-sm mb-6">Toute modification est appliquée immédiatement (pas de re-validation).</p>
      {activities.length === 0 ? <p className="text-zinc-400 text-center py-12">Aucune activité</p> : <div className="space-y-3">{rows}</div>}
    </Card>
  );
}

// Paiements mis en revue par Radar : l'admin approuve ou refuse DANS l'appli.
// Refuser = rembourser le voyageur + annuler la réservation (côté serveur).
function PaymentReviews() {
  var [items, setItems] = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [busy, setBusy] = React.useState(null);

  function load() {
    setLoading(true);
    axios.get(API_URL + '/api/admin/payment-reviews', { withCredentials: true })
      .then(function (r) { setItems(r.data.items || []); })
      .catch(function () { setItems([]); })
      .finally(function () { setLoading(false); });
  }
  React.useEffect(load, []);

  function act(review, kind) {
    if (kind === 'refuse' && !window.confirm('Refuser ce paiement ? Le voyageur sera remboursé et la réservation annulée.')) return;
    setBusy(review.review_id);
    axios.post(API_URL + '/api/admin/payment-reviews/' + review.review_id + '/' + kind, {}, { withCredentials: true })
      .then(function () { toast.success(kind === 'approve' ? 'Paiement approuvé' : 'Paiement refusé et remboursé'); load(); })
      .catch(function (e) { toast.error((e.response && e.response.data && e.response.data.detail) || 'Erreur'); })
      .finally(function () { setBusy(null); });
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-8" data-testid="payment-reviews">
      <div className="flex items-center gap-3 mb-2">
        <AlertTriangle className="h-6 w-6 text-[#F9004D]" />
        <h2 className="text-2xl font-bold text-white">Paiements à vérifier</h2>
      </div>
      <p className="text-zinc-400 text-sm mb-6">
        Paiements signalés par la protection anti-fraude (Radar). Approuvez ceux qui sont légitimes,
        refusez les suspects (remboursement + annulation automatiques).
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /> Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-zinc-500 py-8 text-center">Aucun paiement en attente de vérification. ✅</div>
      ) : (
        <div className="space-y-3">
          {items.map(function (r) {
            return (
              <div key={r.review_id} className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-lg bg-black/30 border border-white/10">
                <div>
                  <p className="text-white font-semibold">
                    {r.amount != null ? (r.amount / 100).toLocaleString() + ' ' + (r.currency || '') : 'Montant inconnu'}
                  </p>
                  <p className="text-zinc-400 text-sm">
                    {r.traveler_email || 'voyageur inconnu'}
                    {r.start_date ? ' · sortie le ' + r.start_date : ''}
                    {r.reason ? ' · ' + r.reason : ''}
                  </p>
                  {r.booking_id && <p className="text-zinc-600 text-xs mt-1">Réservation {r.booking_id}</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" disabled={busy === r.review_id} onClick={function () { act(r, 'approve'); }}
                    className="bg-green-600 hover:bg-green-700 text-white" data-testid="review-approve">
                    <Check className="h-4 w-4 mr-1" /> Approuver
                  </Button>
                  <Button size="sm" disabled={busy === r.review_id} onClick={function () { act(r, 'refuse'); }}
                    variant="outline" className="border-[#F9004D]/60 text-[#F9004D] hover:bg-[#F9004D]/10" data-testid="review-refuse">
                    <X className="h-4 w-4 mr-1" /> Refuser
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default function AdminDashboard() {
  var location = useLocation();
  var navigate = useNavigate();
  // Compteur « à valider » pour la pastille de la barre latérale (retour visuel).
  var reviewCountState = React.useState(0);
  var reviewCount = reviewCountState[0];
  var setReviewCount = reviewCountState[1];
  React.useEffect(function () {
    var stop = false;
    function poll() {
      axios.get(API_URL + '/api/admin/payment-reviews', { withCredentials: true })
        .then(function (r) { if (!stop) setReviewCount(r.data.count || 0); })
        .catch(function () {});
    }
    poll();
    var id = setInterval(poll, 120000);   // toutes les 2 min
    return function () { stop = true; clearInterval(id); };
  }, []);

  function isActive(path) { return location.pathname === path; }

  var sidebarLinks = [
    { to: '/admin/dashboard', icon: TrendingUp, label: 'Statistiques' },
    { to: '/admin/applications', icon: FileCheck, label: 'Candidatures' },
    { to: '/admin/activities', icon: MapPin, label: 'Validation Activites' },
    { to: '/admin/edit-activities', icon: Pencil, label: 'Modifier Activites' },
    { to: '/admin/photos', icon: ImageIcon, label: 'Photos Activites' },
    { to: '/admin/bookings', icon: CreditCard, label: 'Reservations' },
    { to: '/admin/payment-reviews', icon: AlertTriangle, label: 'Paiements a verifier', badge: reviewCount },
    { to: '/admin/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/admin/hosts', icon: UserCheck, label: 'Guides' },
    { to: '/admin/travelers', icon: ShoppingBag, label: 'Voyageurs' },
    { to: '/admin/admins', icon: Shield, label: 'Administrateurs' },
    { to: '/admin/payouts', icon: Banknote, label: 'Virements' },
    { to: '/admin/promo', icon: Tag, label: 'Codes promo' },
    { to: '/admin/wallets', icon: Wallet, label: 'Cagnottes' },
    { to: '/admin/videos', icon: Film, label: 'Videos' }
  ];

  var sidebarButtons = [];
  for (var i = 0; i < sidebarLinks.length; i++) {
    var link = sidebarLinks[i];
    var IconComp = link.icon;
    var active = isActive(link.to);
    sidebarButtons.push(
      <Link key={link.to} to={link.to}>
        <Button variant="ghost" className={'w-full justify-start ' + (active ? 'bg-[#F9004D] text-white' : 'text-zinc-300 hover:text-white')} data-testid={'admin-nav-' + link.label.toLowerCase().replace(/\s/g, '-')}>
          <IconComp className="h-5 w-5 mr-3" />
          <span className="flex-1 text-left">{link.label}</span>
          {link.badge > 0 && (
            <span className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-[#F9004D] text-white text-xs font-bold flex items-center justify-center" data-testid="admin-review-badge">
              {link.badge > 9 ? '9+' : link.badge}
            </span>
          )}
        </Button>
      </Link>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6">
        <h1 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Syne, sans-serif' }}>Admin Dashboard</h1>
        <div className="flex gap-8">
          <aside className="w-64 space-y-2">
            {sidebarButtons}
            <div className="pt-6 mt-6 border-t border-white/10">
              <p className="text-xs text-zinc-500 mb-3 px-3">TESTER LES VUES</p>
              <Button onClick={function() { navigate('/guide/dashboard'); }} variant="outline" className="w-full justify-start border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10" data-testid="view-as-host">
                <Eye className="h-5 w-5 mr-3" /> Voir comme Guide
              </Button>
              <Button onClick={function() { navigate('/activities'); }} variant="outline" className="w-full justify-start mt-2 border-zinc-600 text-zinc-300 hover:bg-zinc-800" data-testid="view-as-traveler">
                <Eye className="h-5 w-5 mr-3" /> Voir comme Voyageur
              </Button>
            </div>
          </aside>
          <main className="flex-1">
            <Routes>
              <Route path="/dashboard" element={<AdminStats />} />
              <Route path="/applications" element={<PendingApplications />} />
              <Route path="/activities" element={<ActivityValidation />} />
              <Route path="/edit-activities" element={<AdminActivityEditor />} />
              <Route path="/photos" element={<ActivityPhotosManager />} />
              <Route path="/bookings" element={<ManageBookings />} />
              <Route path="/payment-reviews" element={<PaymentReviews />} />
              <Route path="/messages" element={<AdminConversations />} />
              <Route path="/hosts" element={<ManageHosts />} />
              <Route path="/travelers" element={<ManageTravelers />} />
              <Route path="/admins" element={<ManageAdmins />} />
              <Route path="/payouts" element={<AdminPayouts />} />
              <Route path="/promo" element={<ManagePromoCodes />} />
              <Route path="/wallets" element={<ManageWallets />} />
              <Route path="/videos" element={<VideoModeration />} />
              <Route path="/" element={<AdminStats />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}
